<script lang="ts">
	import { env as publicEnv } from '$env/dynamic/public';
	import { goto } from '$app/navigation';
	import { page } from '$app/state';
	import Container from '$lib/components/App/Container.svelte';
	import { t } from '@riva-mono/common';
	import { getContext, onMount } from 'svelte';
	import type { ErrorStoreType } from '$lib/stores/errorStore';

	import ItsMeButton from '$lib/components/Questionnaire/ItsMeButton.svelte';
	import { loadCompleteUser } from '$lib/database/manager.js';
	import type { UserStore } from '$lib/stores/user.js';

	let { data } = $props();

	const errorStore: ErrorStoreType = getContext('error');
	const clientId = publicEnv.PUBLIC_ITSME_CLIENT_ID;
	const redirectUrl = publicEnv.PUBLIC_ITSME_REDIRECT_URL;
	const service = publicEnv.PUBLIC_ITSME_SERVICE;
	const connectUrl = publicEnv.PUBLIC_ITSME_CONNECT_URL;
	const userStore: UserStore = getContext('user');

	let loading = $state(false);

	async function checkSession(code: string | null, redirectTo: string | null) {
		if (code && !loading) {
			loading = true;
			const sessionRes = await fetch('/api/session', {
				method: 'POST',
				body: JSON.stringify({ code, lang: page.params.lang })
			});

			// receive token to set sesssion
			const json = await sessionRes.json();

			if (sessionRes.status !== 200) {
				loading = false;
				console.error(json.message + '  ' + sessionRes?.status);
				errorStore.setError({
					title: $t('login.error_session'),
					description: json.message + '  ' + sessionRes?.status
				});
				return;
			}

			const authResponse = await data.supabase.auth.setSession({
				access_token: json.token,
				refresh_token: json.token
			});
			if (authResponse.error) {
				loading = false;
				errorStore.setError({
					title: $t('login.error_session'),
					description: authResponse.error.message || 'Error auth response'
				});
				return;
			}

			if (!authResponse.data) {
				loading = false;
				errorStore.setError({
					title: $t('login.error_session'),
					description: 'No data in authResponse'
				});
				return;
			}

			const session = authResponse.data.session;

			if (data.supabase && session) {
				const fromUrl = sessionStorage.getItem('redirectTo');
				sessionStorage.removeItem('redirectTo');
				if (fromUrl && fromUrl !== page.url.href) {
					goto(fromUrl, { replaceState: true, invalidateAll: true });
				} else {
					goto(`/${page.params.lang}/booking`, { replaceState: true, invalidateAll: true });
				}
			} else {
				loading = false;
				console.error('No session found', session);
				errorStore.setError({ description: $t('login.error_session') });
			}
		} else {
			const error = page.url.searchParams.get('error');
			if (error) {
				errorStore.setError({
					description: $t('login.error_itsme_generic'),
					title: $t('common.sorry')
				});
			}
		}
		loading = false;
	}

	onMount(() => {
		const redirectTo = page.url.searchParams.get('from');
		if (redirectTo) {
			sessionStorage.setItem('redirectTo', redirectTo);
		}
		if (data.session) {
			if (redirectTo) {
				goto(redirectTo, { replaceState: true });
			} else {
				goto(`/${page.params.lang}/booking`, { replaceState: true });
			}
		} else {
			const code = page.url.searchParams.get('code');
			checkSession(code, redirectTo);
		}
	});
</script>

<Container style="padding-left: var(--spacing-l); justify-content:center;animation:unset;">
	<h1>{$t('login.title')}</h1>
	<h2>{$t('login.subtitle')}</h2>
	<div style="display:flex; gap:0.5rem; flex-direction:column;">
		<div class="list-item">
			<img
				src="/icons/check-circle-icon.svg"
				alt="green check in a circle"
				width="24"
				height="24"
			/>
			<p>{@html $t('login.item_1')}</p>
		</div>
		<div class="list-item">
			<img
				src="/icons/check-circle-icon.svg"
				alt="green check in a circle"
				width="24"
				height="24"
			/>
			<p>{@html $t('login.item_2')}</p>
		</div>
		<div class="list-item">
			<img
				src="/icons/check-circle-icon.svg"
				alt="green check in a circle"
				width="24"
				height="24"
			/>
			<p>{@html $t('login.item_3')}</p>
		</div>
	</div>
	<a
		href={`${connectUrl}?client_id=${clientId}&response_type=code&scope=service:${service}%20openid%20email%20phone%20eid%20address%20profile&redirect_uri=${redirectUrl}&claims={"id_token":{"name":null,"gender":null,"family_name":null,"birthdate":null,"address":null,"http://itsme.services/v2/claim/BEeidSn":null,"http://itsme.services/v2/claim/BENationalNumber":null}}`}
	>
		<ItsMeButton {loading} />
	</a>
	<p>
		<!-- {@html $t('login.create_itsme')} -->
		{@html $t('login.create_itsme_phone')}
		<br />
		{@html $t('login.support')}
	</p>
</Container>

<style>
	p {
		margin: 0;
	}

	h1 {
		max-width: 750px;
		text-wrap: balance;
		font-size: var(--largeTitle);
		line-height: 1em;
		font-weight: 500;
		line-height: 1em;
	}
	h2 {
		font-size: 1.2em;
		font-weight: 700;
		opacity: 0.5;
	}
	.list-item {
		display: flex;
		align-items: flex-start;
		gap: 0.5rem;
	}
</style>
